import {matchPath, useLocation, useNavigate} from "react-router";
import {useTranslation} from "react-i18next";
import {type ReactNode, useEffect, useState} from "react";
import {ErrorRounded} from "@mui/icons-material";
import {useAppSelector} from "../../../hooks/reduxHooks.ts";
import {useRoutesByTag} from "../../../service/routeProvider.tsx";
import useDirtyLeaveCheck from "../../../hooks/useDirtyLeaveCheck.ts";

interface ListItemLinkProps {
    icon: ReactNode;
    to: string;
    label: string;
    /** Translation key of what needs the user's attention there; marks the entry. */
    alert?: string;
}

const ListItemLink = (props: ListItemLinkProps) => {
    const {icon, to, label, alert} = props;
    const {pathname} = useLocation();
    const isActive = matchPath({path: to, end: to === "/"}, pathname);
    const [redirected, setRedirected] = useState<string | null>(null);
    const {t} = useTranslation();
    const navigate = useNavigate();
    const agreementFaulty = useAppSelector(state => state.auth.agreementFaulty);
    const {denyDirtyLeave, dialog: confirmDialog} = useDirtyLeaveCheck();
    const dpaRoutes = useRoutesByTag("DPA");

    useEffect(() => {
        if (agreementFaulty) {
            if (dpaRoutes.length === 1 && !to.includes(dpaRoutes[0].path)) {
                setRedirected(dpaRoutes[0].path);
            }
        } else {
            setRedirected(null);
        }
    }, [agreementFaulty, to, dpaRoutes]);

    const handleNavigate = async () => {
        if (await denyDirtyLeave()) return;
        if (redirected) {
            void navigate(redirected);
        } else {
            void navigate(to);
        }
    };

    const cursor = redirected ? "cursor-not-allowed" : "cursor-pointer";
    // Active = brand-mint text + raised surface bg. Subtle surface-vs-panel
    // contrast alone read as "disabled" in dark mode; brand-tinted text makes
    // "selected" unambiguous in both modes.
    // Inactive needs explicit `bg-transparent` because `color-scheme: dark`
    // makes Chrome render a default gray button background otherwise.
    // An entry that needs attention is tinted whether it is active or not: red text
    // alone read as a hint, not as something to act on.
    const surfaceClass = alert
        ? "bg-(--an-warning-bg) shadow-an-xs ring-1 ring-inset ring-(--an-warning-border)"
        : isActive
            ? "bg-an-surface shadow-an-xs"
            : "bg-transparent hover:bg-an-surface/40";

    // The alert color replaces the state color instead of being appended to it: both
    // are equally specific utilities, so whichever Tailwind emits later would win.
    const textClass = alert
        ? "text-an-error font-bold"
        : isActive
            ? "text-an-heading font-bold"
            : "text-an-muted-2 hover:text-an-ink";

    const title = alert ? `${t(label)} – ${t(alert)}` : t(label);

    return (
        <li>
            <button
                type="button"
                onClick={() => { void handleNavigate(); }}
                title={title}
                aria-label={title}
                className={`${cursor} ${surfaceClass} ${textClass} w-full min-w-0 min-h-12 flex items-center sm:gap-3 sm:px-3 py-2.5 rounded-an-sm transition-colors duration-150 ease-out font-lato text-base text-left border-0`}
            >
                {icon && (
                    <span className="relative shrink-0 mx-auto sm:mx-0 inline-flex items-center justify-center [&_.MuiSvgIcon-root]:text-[24px]">
                        {icon}
                        {/* The dot is what is left of the alert on the collapsed phone sidebar. */}
                        {alert && (
                            <span
                                aria-hidden
                                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-an-rose-500 ring-2 ring-(--an-warning-bg)"
                            />
                        )}
                    </span>
                )}
                <span className="hidden sm:block flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis">
                    {t(label)}
                </span>
                {alert && (
                    <ErrorRounded aria-hidden className="hidden sm:block shrink-0" sx={{fontSize: 20, color: "var(--an-warning-icon)"}}/>
                )}
            </button>
            {confirmDialog}
        </li>
    );
};

export default ListItemLink;
